import React, { useMemo } from 'react';
import Product from '../components/Product';
import Variants from '../components/CheckoutForm/components/Variants';
import CheckoutForm from '../components/CheckoutForm';
import useCheckoutState from '../hooks/useCheckoutState';
import { checkHasEventModules, checkHasProductModules } from '../enums/modules';
import useCartCost from '../hooks/useCartCost';

import clsx from 'clsx';
import Button from '../components/Button';
import PoweredByStripe from '../components/PoweredByStripe/PoweredByStripe';
import CheckoutConfirmation from '../CheckoutConfirmation';
import CheckoutFormContainer from '../components/CheckoutFormContainer';
import Media from '../components/Media';
import checkHasVisibleVariants from '../lib/checkHasVisibleVariants';
import { PAGE_TYPES } from '../enums/page-types';
import { getMedia } from '../lib/getMedia';
import EventDetails from '../components/EventDetails';
import TicketGroups from '../components/TicketGroups';
import Divider from '../components/Divider';
import getUrlFromCdn, { CDNFitOption } from '../services/getUrlFromCdn';

export default function CheckoutCenter() {
  const {
    checkoutType,
    product,
    checkout,
    seller,
    query,
    isTesting,
    isPreview,
    isTestingPayments,
    form,
    isScreenshot,
    isConfirmation,
  } = useCheckoutState();
  const { productPriceAmount, setupFeeAmount } = useCartCost();
  const hasTicketTypes = checkout.type === PAGE_TYPES.EVENT;
  const hasVisibleVariants = checkHasVisibleVariants(
    product.variants,
    form.variants,
    query,
    isTesting,
  );

  const hasProductModules = checkHasProductModules({
    checkout,
    seller,
    query,
    isScreenshot,
    productPriceAmount,
    setupFeeAmount,
    discountedFromPrice: product.discountedFromPrice,
    trialPeriodDays: product.trialPeriodDays,
    startDate: product.startDate,
  });

  const hasEventModules = checkHasEventModules({ checkout, seller });

  const isForm = checkout.type === PAGE_TYPES.FORM;
  const isEvent = checkout.type === PAGE_TYPES.EVENT;

  const media = getMedia({ checkout, product });
  const hasMedia = media && media.length > 0;

  const defaultWidth = 1920;
  const defaultHeight = 1080;

  const checkoutClass = useMemo(
    () =>
      clsx('cp_checkout cp_checkout-center relative', {
        'my-8 px-4': checkoutType !== 'embed',
      }),
    [checkoutType],
  );

  const cardClass = useMemo(
    () =>
      clsx('cp_card mx-auto w-full bg-white relative z-10', {
        'rounded shadow max-w-screen-xs': checkoutType !== 'embed',
      }),
    [checkoutType],
  );


  return (
    <div className={checkoutClass}>
      <div className={cardClass}>
        {isConfirmation && <CheckoutConfirmation />}

        {!isConfirmation && (
          <CheckoutFormContainer>
            {hasMedia && (
              <div className={clsx('cp_product-image-wrapper space-y-4 mb-4')}>
                <Media media={media} />
              </div>
            )}

            {hasProductModules && (
              <div className="px-4">
                <Product />
              </div>
            )}

            {hasEventModules && (
              <div className="px-4">
                <EventDetails />
              </div>
            )}

            {hasTicketTypes && (
              <div
                className={clsx('cp_ticket-types-wrapper', {
                  'mt-4': hasEventModules,
                  'pt-4': !hasEventModules,
                })}
              >
                <TicketGroups />
              </div>
            )}

            {hasVisibleVariants && (
              <div
                className={clsx('cp_variants-wrapper mb-4', {
                  'mt-4': hasProductModules,
                  'pt-4': !hasProductModules,
                })}
              >
                <Variants />
              </div>
            )}

            {!hasVisibleVariants && hasProductModules && <Divider padded />}

            <div
              className={clsx('px-4', {
                'pt-4':
                  (isForm && !hasProductModules) ||
                  (!isForm && !hasVisibleVariants && !hasProductModules) ||
                  (isEvent && !hasTicketTypes && !hasEventModules),
              })}
            >
              <CheckoutForm />
            </div>
          </CheckoutFormContainer>
        )}
      </div>

      {(checkout.type === PAGE_TYPES.CHECKOUT || checkout.type === PAGE_TYPES.EVENT) &&
        checkout.showPoweredByStripe && (
          <div className="cp_powered-by-stripe-wrapper py-2 w-full flex justify-center">
            <Button
              to="https://stripe.com"
              target="_blank"
              appearance="bare"
              className="cp_powered-by-stripe-link"
            >
              <PoweredByStripe style={checkout.poweredByStripeStyle} />
            </Button>
          </div>
        )}

      {checkoutType !== 'embed' &&
        checkoutType !== 'popup' &&
        (!isPreview || isTestingPayments) && (
          <div
            className="fixed h-screen w-screen inset-0 bg-fixed bg-no-repeat bg-cover bg-center -z-[1]"
            style={{
              ...(checkout.background === 'image' && checkout.backgroundImage?.url
                ? {
                  backgroundImage: `image-set(url("${getUrlFromCdn(checkout.backgroundImage.url, {
                    width: defaultWidth,
                    height: defaultHeight,
                    fit: CDNFitOption.cover,
                  })}"), url("${getUrlFromCdn(checkout.backgroundImage.url, {
                    width: defaultWidth * 2,
                    height: defaultHeight * 2,
                    fit: CDNFitOption.cover,
                  })}") 2x)`,
                }
                : {}),
              ...(checkout.background === 'color' && checkout.backgroundColor
                ? { background: checkout.backgroundColor }
                : {}),
            }}
          />
        )}
    </div>
  );
}
